import quickFetch from 'quick-fetch';
import { makeQuickLoadRequest } from 'persist-promise/quickLoad';
import { makeTypeMetadataEntryFetchKey, quickFetchTypeMetadataEntries } from './quickFetchFunctions/typeMetadata';
import { getDefinitionsFromObjectTypeEarlyRequestRpc } from '../__generated__/chirp/com/hubspot/association/definitions/rpc/earlyRequest/AssociationDefinitionsRpc.early';
import { makeChirpEarlyRequest } from 'rpc-client-utils';
export const makeGroupsWithPropertiesQuickFetchKey = ({
  portalId = quickFetch.getPortalId(),
  frameworkTypeIdentifier,
  query
}) => `frameworkDataSchemaResolvers-${portalId}-properties-${frameworkTypeIdentifier}-${(query === null || query === void 0 ? void 0 : query.showHighlySensitiveProperties) || false}`;
export const getGroupsWithPropertiesFetchUrl = ({
  frameworkTypeIdentifier,
  query
}) => `/properties/v4/groups/${encodeURIComponent(frameworkTypeIdentifier)}/properties?includeFieldLevelPermission=true${query !== null && query !== void 0 && query.showHighlySensitiveProperties ? '&showHighlySensitiveProperties=true' : ''}`;
export const makeFrameworkTypesQuickFetchKey = ({
  portalId = quickFetch.getPortalId(),
  family
}) => `frameworkDataSchemaResolvers-${portalId}-frameworkTypes-${family}`;
const makeObjectTypesFetchUrl = () => '/customer-object-types/v1/for-portal';
const makeEventTypesFetchUrl = () => '/inbounddb-meta/v1/object-types/events/for-portal';
export const makeFrameworkTypesFetchUrl = ({
  family
}) => family === 'objects' ? makeObjectTypesFetchUrl() : makeEventTypesFetchUrl();
export const makePropertyMetadataEntriesFetchKey = ({
  frameworkTypeIdentifier,
  appSettingName,
  portalId = quickFetch.getPortalId()
}) => `frameworkDataSchemaResolvers-${portalId}-propertyMetadataEntries-${frameworkTypeIdentifier}-${appSettingName}`;
export const makePropertyMetadataEntriesFetchUrl = ({
  frameworkTypeIdentifier,
  appSettingName
}) => `/framework-builder/v1/read/metadata/property/${encodeURIComponent(frameworkTypeIdentifier)}/${encodeURIComponent(appSettingName)}`;
export const makePipelinesFetchKey = ({
  frameworkTypeIdentifier,
  portalId = quickFetch.getPortalId()
}) => `frameworkDataSchemaResolvers-${portalId}-pipelines-${frameworkTypeIdentifier}`;
export const makePipelinesFetchUrl = ({
  frameworkTypeIdentifier
}) => `/pipelines/v2/pipelines/${encodeURIComponent(frameworkTypeIdentifier)}?includeApprovalStageStatus=true&includePermissions=true`;
export const makeAssociationDefinitionsFetchKey = ({
  frameworkTypeIdentifier
}) => `associationDefinitions-${frameworkTypeIdentifier}`;
export const makeMultiCurrencyFetchKey = ({
  portalId = quickFetch.getPortalId()
} = {}) => `frameworkDataSchemaResolvers-${portalId}-multiCurrency`;
export const makeMultiCurrencyFetchUrl = () => '/multi-currency/v2/currencies/information';
const doQuickLoad = (requestName, entityName) => makeQuickLoadRequest({
  namespace: 'FDSR',
  entityName,
  cacheKey: requestName
});

/**
 *
 * @param options.typeMetadataDebounceInterval Determines the debounce interval used to batch type metadata requests together. Each
 * instance has its own typeMetadata debounce and request queue.
 * @returns A promise that resolves to a FDSR quick-fetch client instance.
 */
export const buildQuickFetchClientInstance = ({
  typeMetadataDebounceInterval = 50
} = {}) => {
  const queuedTypeMetadataRequests = new Set();
  let typeMetadataRequestTimeout;
  const quickFetchGroupsWithPropertiesForType = ({
    frameworkTypeIdentifier,
    query: userQuery
  }) => {
    // HACK: showHighlySensitiveProperties: false (the default) sets the properties BE to hide
    // all properties marked as highly sensitive. This is very wasteful — only a small fraction
    // of properties are highly sensitive, and the only change is their `hidden` flag, but apps/libraries
    // frequently need both sides of the query param.
    //
    // To avoid making two requests, we'll make the request with showHighlySensitiveProperties: true
    // and then duplicate the BE's logic in the properties client. So, no matter what they requested,
    // we'll pass `true` here so that we get the true `hidden` value of the properties, and set their
    // `hidden` flags accordingly based on what the user wanted.
    const query = Object.assign({}, userQuery, {
      showHighlySensitiveProperties: true
    });
    const requestName = makeGroupsWithPropertiesQuickFetchKey({
      frameworkTypeIdentifier,
      query
    });
    doQuickLoad(requestName, 'properties');
    if (quickFetch.getRequestStateByName(requestName)) {
      return;
    }
    quickFetch.makeEarlyRequest(requestName, {
      url: quickFetch.getApiUrl(getGroupsWithPropertiesFetchUrl({
        frameworkTypeIdentifier,
        query
      }), true),
      dataType: 'json',
      contentType: 'application/json',
      type: 'GET'
    });
  };
  const makeQuickFetchFrameworkTypesForFamily = ({
    family
  }) => () => {
    const requestName = makeFrameworkTypesQuickFetchKey({
      family
    });
    doQuickLoad(requestName, 'frameworkTypes');
    if (quickFetch.getRequestStateByName(requestName)) {
      return;
    }
    quickFetch.makeEarlyRequest(requestName, {
      url: quickFetch.getApiUrl(makeFrameworkTypesFetchUrl({
        family
      }), true),
      dataType: 'json',
      contentType: 'application/json',
      type: 'GET'
    });
  };
  const quickFetchPropertyMetadataEntries = ({
    frameworkTypeIdentifier,
    appSettingName
  }) => {
    const requestName = makePropertyMetadataEntriesFetchKey({
      frameworkTypeIdentifier,
      appSettingName
    });
    doQuickLoad(requestName, 'propertyMetadata');
    if (quickFetch.getRequestStateByName(requestName)) {
      return;
    }
    quickFetch.makeEarlyRequest(requestName, {
      url: quickFetch.getApiUrl(makePropertyMetadataEntriesFetchUrl({
        frameworkTypeIdentifier,
        appSettingName
      }), true),
      dataType: 'json',
      contentType: 'application/json',
      type: 'GET'
    });
  };
  const quickFetchPipelines = ({
    frameworkTypeIdentifier
  }) => {
    const requestName = makePipelinesFetchKey({
      frameworkTypeIdentifier
    });
    doQuickLoad(requestName, 'pipelines');
    if (quickFetch.getRequestStateByName(requestName)) {
      return;
    }
    quickFetch.makeEarlyRequest(requestName, {
      url: quickFetch.getApiUrl(makePipelinesFetchUrl({
        frameworkTypeIdentifier
      }), true),
      dataType: 'json',
      contentType: 'application/json',
      type: 'GET'
    });
  };
  const quickFetchMultiCurrency = () => {
    const requestName = makeMultiCurrencyFetchKey({});
    doQuickLoad(requestName, 'multiCurrency');
    if (quickFetch.getRequestStateByName(requestName)) {
      return;
    }
    quickFetch.makeEarlyRequest(requestName, {
      url: quickFetch.getApiUrl(makeMultiCurrencyFetchUrl(), true),
      dataType: 'json',
      contentType: 'application/json',
      type: 'GET'
    });
  };
  const client = {
    properties: {
      get: quickFetchGroupsWithPropertiesForType,
      getGroups: quickFetchGroupsWithPropertiesForType,
      // Preserves the `getProperty` interface just in case we ever
      // switch this to actually fetch individual properties (very unlikely)
      getProperty: ({
        frameworkTypeIdentifier,
        propertyName: __propertyName
      }) => quickFetchGroupsWithPropertiesForType({
        frameworkTypeIdentifier
      })
    },
    pipelines: {
      get: quickFetchPipelines,
      getPipeline: ({
        frameworkTypeIdentifier
      }) => quickFetchPipelines({
        frameworkTypeIdentifier
      })
    },
    associations: {
      get: ({
        frameworkTypeIdentifier
      }) => {
        doQuickLoad(makeAssociationDefinitionsFetchKey({
          frameworkTypeIdentifier
        }), 'associationDefinitions');
        makeChirpEarlyRequest(getDefinitionsFromObjectTypeEarlyRequestRpc, {
          request: {
            objectType: frameworkTypeIdentifier,
            definitionRequestOptions: {
              enforceStrictPermissions: true
            }
          }
        }).catch(() => {
          // Do nothing — this is a perf optimization, we'll try again in app code
        });
      }
    },
    frameworkTypes: {
      getEvents: makeQuickFetchFrameworkTypesForFamily({
        family: 'events'
      }),
      getObjects: makeQuickFetchFrameworkTypesForFamily({
        family: 'objects'
      })
    },
    typeMetadata: {
      get: ({
        appSettingNames
      }) => {
        // Type metadata is split out and cached by individual app setting name, so we need to
        // fire off loads for each app setting. doQuickLoad automatically ignores requests
        // that have already been fired, so we can just blindly fire them off without debouncing.
        appSettingNames.forEach(appSettingName => doQuickLoad(makeTypeMetadataEntryFetchKey({
          appSettingName
        }), 'typeMetadata'));
        if (typeMetadataRequestTimeout) {
          clearTimeout(typeMetadataRequestTimeout);
        }
        appSettingNames.forEach(name => queuedTypeMetadataRequests.add(name));

        // Straightforward debouncing approach. Collects all requests on a 50ms debounce,
        // then fires the request. This should generally catch the case where multiple QF clients
        // are constructed and used independently in a QF file
        typeMetadataRequestTimeout = setTimeout(() => {
          quickFetchTypeMetadataEntries({
            appSettingNames: [...queuedTypeMetadataRequests]
          });

          // Clear request queue
          queuedTypeMetadataRequests.clear();
        }, typeMetadataDebounceInterval);
      }
    },
    propertyMetadata: {
      get: quickFetchPropertyMetadataEntries
    },
    multiCurrency: {
      get: quickFetchMultiCurrency
    },
    graph: {
      frameworkTypes: {
        getObjects: ({
          appSettingNames
        } = {}) => {
          client.frameworkTypes.getObjects();
          if (appSettingNames && appSettingNames.length) {
            client.typeMetadata.get({
              appSettingNames
            });
          }
        },
        getEvents: ({
          appSettingNames
        } = {}) => {
          client.frameworkTypes.getEvents();
          if (appSettingNames && appSettingNames.length) {
            client.typeMetadata.get({
              appSettingNames
            });
          }
        }
      },
      properties: {
        getGroups: ({
          frameworkTypeIdentifier,
          appSettingNames
        }) => {
          client.properties.getGroups({
            frameworkTypeIdentifier
          });
          appSettingNames.forEach(appSettingName => client.propertyMetadata.get({
            frameworkTypeIdentifier,
            appSettingName
          }));
        },
        getProperty: ({
          frameworkTypeIdentifier,
          propertyName: __propertyName,
          appSettingNames
        }) => {
          client.graph.properties.getGroups({
            frameworkTypeIdentifier,
            appSettingNames
          });
        },
        get: ({
          frameworkTypeIdentifier,
          appSettingNames
        }) => {
          client.graph.properties.getGroups({
            frameworkTypeIdentifier,
            appSettingNames
          });
        }
      }
    }
  };
  return Promise.resolve(client);
};